import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const CAREERS_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://phennx.io/careers#webpage',
      url: 'https://phennx.io/careers',
      name: 'Careers at Phennx',
      description:
        'Join Phennx to build the clinical research platform that keeps trial operations accountable, compliant, and visible.',
      isPartOf: { '@id': 'https://phennx.io/#website' },
      about: { '@id': 'https://phennx.io/#organization' },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': 'https://phennx.io/careers#breadcrumbs',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://phennx.io/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Careers',
          item: 'https://phennx.io/careers',
        },
      ],
    },
  ],
};

const IMPACT_PILLARS = [
  {
    title: 'Operator-led product',
    description:
      'Work alongside clinical operators who have run trials and know what it takes to keep teams audit-ready.',
  },
  {
    title: 'Outcome-driven delivery',
    description:
      'Ship workflows that reduce administrative burden, improve visibility, and support faster, safer studies.',
  },
  {
    title: 'Engineering with purpose',
    description:
      'Build reliable systems that keep compliance front-and-center while staying usable for busy study teams.',
  },
];

const BENEFITS = [

];

const OPEN_ROLES = [
  {
    title: 'General Interest',
    team: 'Cross-functional',
    location: 'Florida (US)',
    description:
      'Tell us where you want to make the biggest impact. We are always interested in meeting mission-aligned talent.',
  },
];

export default function CareersPage() {
  return (
    <>
      <SEO
        title="Careers at Phennx"
        description="Join Phennx to build the clinical research platform that keeps trial operations accountable, compliant, and visible."
        path="/careers"
        image="/logo-full-transparent.png"
        type="website"
        schema={CAREERS_SCHEMA}
      />
      <div className="careers-page">
        <header className="careers-hero" aria-label="Careers at Phennx">
          <div className="careers-hero__glow" aria-hidden="true" />
          <div className="section-inner careers-hero__inner">
            <div className="careers-hero__copy">
              <div className="careers-hero__eyebrow">Careers</div>
              <h1 className="careers-hero__title">Build the clinical operations platform teams rely on.</h1>
              <p className="careers-hero__subtitle">
                Phennx blends clinical operations experience with modern engineering. Join a team focused on accountability,
                clarity, and faster study execution.
              </p>
              <div className="careers-hero__actions">
                <Link to="/#contact" className="btn-primary careers-hero__cta">
                  Talk with us
                </Link>
                <a href="#open-roles" className="btn-secondary careers-hero__cta">
                  See open roles
                </a>
              </div>
            </div>

            <div className="careers-hero__panel">
              <div className="careers-hero__panel-title">How we work</div>
              <ul className="careers-hero__panel-list">
                <li>Direct access to clinical operators and compliance leaders.</li>
                <li>Small squads with end-to-end ownership.</li>
                <li>Focus on reliability, security, and measurable outcomes.</li>
              </ul>
            </div>
          </div>
        </header>

        <section className="section careers-values" aria-label="Why work at Phennx">
          <div className="section-inner">
            <div className="section-header">
              <h2 className="section-title">Why Phennx</h2>
              <p className="section-subtitle">
                We build for the people running trials every day. Every role contributes directly to operational clarity
                and better patient outcomes.
              </p>
            </div>
            <div className="careers-values__grid" role="list">
              {IMPACT_PILLARS.map((pillar) => (
                <div key={pillar.title} className="careers-value-card" role="listitem">
                  <div className="careers-value-card__title">{pillar.title}</div>
                  <div className="careers-value-card__text">{pillar.description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section careers-benefits" aria-label="Benefits and perks">
          <div className="section-inner careers-benefits__inner">
            <div className="careers-benefits__copy">
              <h2 className="section-title">Benefits that support focus</h2>
              <p className="section-subtitle">
                We keep the team small and high-trust, with benefits that prioritize flexibility and growth.
              </p>
            </div>
            <ul className="careers-benefits__list">
              {BENEFITS.map((benefit) => (
                <li key={benefit} className="careers-benefits__item">
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="section careers-roles" id="open-roles" aria-label="Open roles">
          <div className="section-inner">
            <div className="section-header">
              <h2 className="section-title">Open roles</h2>
              <p className="section-subtitle">
                We keep our hiring focused on high-impact opportunities. If you do not see your role, share your story and
                we will be in touch.
              </p>
            </div>
            <div className="careers-roles__grid" role="list">
              {OPEN_ROLES.length === 0 ? (
                <div className="careers-role-card" role="listitem">
                  <div className="careers-role-card__title">No open roles right now</div>
                  <p className="careers-role-card__text">
                    We are not actively hiring today, but we still want to meet people who care about clinical operations
                    and compliance. Share your background and we will reach out when roles open.
                  </p>
                  <Link to="/#contact" className="btn-secondary careers-role-card__cta">
                    Contact us
                  </Link>
                </div>
              ) : (
                OPEN_ROLES.map((role) => (
                  <div key={role.title} className="careers-role-card" role="listitem">
                    <div className="careers-role-card__title">{role.title}</div>
                    <div className="careers-role-card__meta">
                      <span>{role.team}</span>
                      <span>{role.location}</span>
                    </div>
                    <p className="careers-role-card__text">{role.description}</p>
                    <Link to="/#contact" className="btn-secondary careers-role-card__cta">
                      Start a conversation
                    </Link>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        <section className="section careers-cta" aria-label="Careers contact">
          <div className="section-inner careers-cta__inner">
            <h2 className="careers-cta__title">Ready to build with us?</h2>
            <p className="careers-cta__text">
              We would love to hear how you want to shape clinical trial operations. Send us a note and we will respond.
            </p>
            <Link to="/#contact" className="btn-primary careers-cta__button">
              Reach out
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
